import { useNavigate, useSearch } from '@tanstack/react-router';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { CopyButton } from '@/components/CopyButton';
import { ErrorState } from '@/components/ErrorState';
import { Kicker } from '@/components/Kicker';
import { Skeleton } from '@/components/ui/skeleton';
import { EnvStep } from '@/features/getting-started/EnvStep';
import { FirstBuildStep } from '@/features/getting-started/FirstBuildStep';
import { ReadinessRail } from '@/features/getting-started/ReadinessRail';
import { setupRunbook } from '@/features/getting-started/runbook';
import { seesProviders } from '@/lib/audience';
import { SetupHero } from '@/features/getting-started/SetupHero';
import { StartSomethingNew } from '@/features/getting-started/StartSomethingNew';
import { SetupStepper, SetupStepperChips } from '@/features/getting-started/SetupStepper';
import { AppsStep, ProfilesStep, ProjectsStep, ProvidersStep, type StepProps } from '@/features/getting-started/SetupSteps';
import { envWarning, pageGate } from '@/features/getting-started/stepNotesModel';
import { useSetup, type Setup, type SetupSearch } from '@/features/getting-started/useSetup';

export type GettingStartedSearch = SetupSearch;

const STEPS = [ProvidersStep, ProjectsStep, AppsStep, ProfilesStep, EnvStep, FirstBuildStep];

/** The six step cards, in order (the stepper and the readiness rail link to their ids). */
function StepList({ stepProps }: { stepProps: Omit<StepProps, 'index'> }) {
  return (
    <ol className="flex min-w-0 flex-col gap-4" aria-label="Setup steps">
      {STEPS.map((Step, index) => (
        <li key={index}>
          <Step {...stepProps} index={index} />
        </li>
      ))}
    </ol>
  );
}

function HeroSection({ setup, onSelect }: { setup: Setup; onSelect: (next: GettingStartedSearch) => void }) {
  return (
    <SetupHero
      progress={setup.progress}
      pending={setup.pending}
      states={setup.states}
      projects={setup.projects.data ?? []}
      project={setup.project}
      apps={setup.apps.data}
      app={setup.app}
      onProject={(slug) => onSelect({ project: slug })}
      onApp={(slug) => onSelect({ project: setup.project?.slug, app: slug })}
    />
  );
}

/** Desktop: stepper | cards (| rail from 1280 px). The rail is one element: grid placement moves it under the cards below 1280. */
function SetupColumns({ setup, onSelect }: { setup: Setup; onSelect: (next: GettingStartedSearch) => void }) {
  const bp = useBreakpoint();
  return (
    <div className="grid gap-5 lg:grid-cols-[14.5rem_minmax(0,1fr)] lg:items-start min-[1280px]:grid-cols-[14.5rem_minmax(0,1fr)_16.5rem]">
      {bp === 'desktop' ? (
        <div className="lg:row-span-2 lg:self-stretch">
          <SetupStepper states={setup.states} progress={setup.progress} envWarning={envWarning(setup.facts.envMissing)} />
        </div>
      ) : null}
      <div className="flex min-w-0 flex-col gap-4 lg:col-start-2">
        {bp === 'tablet' ? <SetupStepperChips states={setup.states} progress={setup.progress} /> : null}
        <StepList stepProps={{ setup, onSelect }} />
      </div>
      <ReadinessRail items={setup.readiness} subject={setup.app?.name ?? setup.project?.name ?? 'Hangar'} className="min-w-0 lg:col-start-2 min-[1280px]:col-start-3 min-[1280px]:row-start-1" />
    </div>
  );
}

/**
 * Getting started (Stitch 27): the setup hub. Seven steps, each a card where the team creates, lists, edits and
 * archives what a build needs (Expo accounts, projects, apps, build profiles, env vars, CLI and CI access) and sees
 * the first builds arrive. Progress, the stepper and the readiness rail are computed from the API's data
 * (features/getting-started/steps.ts), for the project and app in the URL (`?project=&app=`).
 */
export function GettingStartedPage() {
  const search = useSearch({ strict: false }) as GettingStartedSearch;
  const navigate = useNavigate();
  const setup = useSetup(search);
  const setSearch = (next: GettingStartedSearch) => void navigate({ to: '/getting-started', search: next, replace: true, resetScroll: false });
  const gate = pageGate(setup.projects, setup.accounts);
  if (gate === 'error') return <ErrorState error={setup.projects.error} onRetry={() => void setup.projects.refetch()} title="Couldn't load your projects" />;
  if (gate === 'loading') return <GettingStartedSkeleton />;
  return (
    <div data-testid="getting-started" className="mx-auto flex w-full max-w-[1200px] flex-col gap-6">
      <SetupHeader setup={setup} />
      <HeroSection setup={setup} onSelect={setSearch} />
      {setup.progress.done === setup.progress.tracked ? <StartSomethingNew project={setup.project} onProject={(slug) => setSearch({ project: slug })} /> : null}
      <SetupColumns setup={setup} onSelect={setSearch} />
    </div>
  );
}

/** Kicker, title, one line, and "Copy setup runbook" (Markdown of the current state, built on click). */
function SetupHeader({ setup }: { setup: Setup }) {
  const desktop = useBreakpoint() === 'desktop';
  // Built on every render from what the page already holds (cheap: a few hundred lines at most).
  const runbook = setupRunbook({
    org: setup.me.data?.org.name,
    project: setup.project,
    app: setup.app,
    projects: setup.projects.data ?? [],
    apps: setup.apps.data ?? [],
    accounts: setup.providerAccounts,
    profiles: setup.profiles.data ?? [],
    envTiles: setup.envTiles,
    states: setup.states,
    progress: setup.progress,
    readiness: setup.readiness,
    consoleUrl: window.location.origin,
    generatedAt: new Date(),
    seesProviders: seesProviders(setup.me.data),
  });
  return (
    <header className="enter flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
      <div className="min-w-0 flex-1 basis-80">
        <Kicker className="text-[11px] text-primary">Getting started</Kicker>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-foreground">Set up Hangar for your team</h1>
        <p className="mt-1 text-sm text-muted-foreground">Every step checks itself against what Hangar already knows. Create, edit or archive things right here.</p>
      </div>
      <CopyButton text={runbook} label={desktop ? 'Copy setup runbook' : 'Copy runbook'} testId="gs-copy-runbook" />
    </header>
  );
}

function GettingStartedSkeleton() {
  return (
    <div data-testid="getting-started-loading" className="mx-auto flex w-full max-w-[1200px] flex-col gap-6" aria-busy="true">
      <div className="flex items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-7 w-80 max-w-full" />
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <Skeleton className="h-8 w-44 max-sm:hidden" />
      </div>
      <Skeleton className="h-28 w-full rounded-lg" />
      <div className="grid gap-5 lg:grid-cols-[14.5rem_minmax(0,1fr)] min-[1280px]:grid-cols-[14.5rem_minmax(0,1fr)_16.5rem]">
        <Skeleton className="h-80 rounded-lg max-lg:hidden" />
        <div className="flex flex-col gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-48 w-full rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-72 rounded-lg max-[1279px]:hidden" />
      </div>
    </div>
  );
}
